"use client";

import { Combobox as BaseCombobox } from "@base-ui/react/combobox";
import { Select as BaseSelect } from "@base-ui/react/select";
import { IconCheck, IconChevronDown, IconX } from "@tabler/icons-react";
import clsx from "clsx";
import type React from "react";
import { useId, useLayoutEffect, useRef, useState } from "react";
import type { SelectOption } from "shared/SelectOption";
import { composeRefs } from "../../../lib/composeRefs";
import type { ControlSize } from "../../../lib/FrontendTypes";
import {
  FLOATING_PANEL_CLASS,
  FLOATING_POPUP_CLASS,
  getFloatingContainer,
  getFloatingPortalProps,
  getFloatingPositionerProps,
} from "../../common/FloatingLayer/FloatingLayer";
import { inputBoxClassName } from "../InputBox/InputBox";
import type { InputProps } from "../InputTypes";
import "./Select.css";

const styles: Record<string, string> = {
  root: "calmlens-select-root",
  control: `${inputBoxClassName} calmlens-select__control`,
  input: "calmlens-select__input",
  value: "calmlens-select__value",
  indicator: "calmlens-select__indicator",
  loading: "calmlens-select__loading",
  menu: "calmlens-select__menu",
  menuList: "calmlens-select__menu-list",
  menuNotice: "calmlens-select__menu-notice",
  option: "calmlens-select__option",
  optionContent: "calmlens-select-optionContent",
  optionLabel: "calmlens-select-optionLabel",
  optionDescription: "calmlens-select-optionDescription",
  selectedIcon: "calmlens-select-selectedIcon",
  menuFooter: "calmlens-select-menuFooter",
  adornment: "calmlens-select__adornment",
  optionRow: "calmlens-select__option-row",
  optionActions: "calmlens-select__option-actions",
};

/** The open state of one option's actions menu, for the menu `renderOptionActions` renders. */
export interface SelectOptionActionsMenu {
  visible: boolean;
  onVisibleChange: (visible: boolean) => void;
  /** Where focus goes when the menu closes: the select's control. */
  finalFocus: () => HTMLElement | null;
}

/** The keys that open the highlighted option's actions, as on a context menu. */
const isActionsKey = (event: React.KeyboardEvent) =>
  event.key === "ContextMenu" || (event.key === "F10" && event.shiftKey);
const stopPropagation = (event: React.SyntheticEvent) => event.stopPropagation();
const optionKey = (option: SelectOption) => `${typeof option.value}:${String(option.value)}`;

export interface SelectProps<O extends SelectOption> extends InputProps, React.AriaAttributes {
  children?: React.ReactNode;
  noOptionsText?: string;
  loadingText?: string;
  options: O[];
  disableSearch?: boolean;
  searchable?: boolean;
  clearable?: boolean;
  /** The clear button's accessible name, in the viewer's language. */
  clearLabel?: string;
  /**
   * Whether the menu renders in place instead of the global popover layer. By default it renders in
   * place inside dialogs, whose focus trap and backdrop block the global layer, and in the layer
   * everywhere else.
   */
  disablePortal?: boolean;
  /** Use "fixed" with disablePortal inside a clipping container, such as a dialog, that is not transformed. */
  menuPosition?: "absolute" | "fixed";
  onOptionChange?: (option: O) => void;
  onSearchChange?: (search: string | number | undefined) => void;
  placeholder?: string;
  /** Whether the menu is open, for a select that closes it itself (such as from a footer action). */
  visible?: boolean;
  onVisibleChange?: (visible: boolean) => void;
  loading?: boolean;
  /** The focusable control: the search input, or the trigger button when search is off. */
  ref?: React.Ref<HTMLElement>;
  size?: ControlSize;
  required?: boolean;
  "aria-label"?: string;
  "aria-describedby"?: string;
  renderOption?: (option: O) => React.ReactNode;
  renderValue?: (option: O) => React.ReactNode;
  /** Shown at the start of the control, before the value or search, such as the value's icon. */
  startAdornment?: React.ReactNode;
  /**
   * Actions for an option, such as a "More" menu button, shown at the end of its row (null for
   * none). Pressing them doesn't select the option. Keyboard users open the highlighted option's
   * menu with Shift+F10 or the context menu key; render the menu with the given open state.
   */
  renderOptionActions?: (option: O, menu: SelectOptionActionsMenu) => React.ReactNode;
  /**
   * Whether typing filters the options here. Turn it off when `onSearchChange` loads the matches
   * (a server search), so options it returns aren't hidden for not containing the text.
   */
  filterOptions?: boolean;
  /** The open list's least width (capped by the viewport), so a narrow control still lists readably. */
  menuMinWidth?: string;
}

const matchesFilter = (option: SelectOption, filter: string) => {
  const normalizedFilter = filter.trim().toLowerCase();
  if (!normalizedFilter) return true;
  return [option.label, option.value, option.description]
    .filter((searchableValue) => searchableValue !== undefined)
    .some((searchableValue) => String(searchableValue).toLowerCase().includes(normalizedFilter));
};

const Select = <O extends SelectOption>(props: SelectProps<O>) => {
  const {
    options,
    onChange,
    onValueChange,
    visible,
    onVisibleChange,
    onOptionChange,
    disableSearch,
    searchable = true,
    clearable = false,
    clearLabel = "Clear selection",
    disablePortal: disablePortalProp,
    menuPosition,
    placeholder,
    value,
    defaultValue,
    className,
    onSearchChange,
    name,
    loading = false,
    ref,
    children,
    size = "md",
    disabled,
    id,
    onFocus,
    onBlur,
    error,
    required,
    renderOption,
    renderValue,
    startAdornment,
    renderOptionActions,
    filterOptions = true,
    menuMinWidth = "18rem",
    noOptionsText = "No results found",
    loadingText = "Loading options...",
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    "aria-describedby": ariaDescribedBy,
    "aria-invalid": ariaInvalid,
  } = props;
  const generatedId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const controlRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const [container, setContainer] = useState<HTMLElement | null>(null);
  useLayoutEffect(() => {
    // The shared popup layer, or inside the enclosing dialog; `disablePortal` renders in place.
    setContainer(disablePortalProp ? rootRef.current : getFloatingContainer(rootRef.current));
  }, [disablePortalProp]);
  const [stateValue, setStateValue] = useState(value ?? defaultValue);
  const resolvedValue = value !== undefined ? value : stateValue;
  const selectedOption = options.find((option) => option.value === resolvedValue) ?? null;
  const resolvedPlaceholder = placeholder ?? "Select an option";
  const inputId = id ?? `select-${generatedId}`;
  // The search text while the user is typing one; otherwise the input shows the selection.
  const [query, setQuery] = useState<string | null>(null);
  const [openState, setOpenState] = useState(false);
  // The option whose actions menu is open. The list stays open under it: the menu renders outside
  // the list, so the list would otherwise close as focus or a press moves into the menu.
  const [actionsKey, setActionsKey] = useState<string | null>(null);
  const open = (visible ?? openState) || actionsKey !== null;
  const openCombobox = () => {
    if (disabled || open) return;
    setOpenState(true);
    handleOpenChange(true);
  };

  const handleChange = (option: O | null) => {
    const nextValue = option?.value;
    setStateValue(nextValue);
    onChange?.({
      target: { value: nextValue, name },
    } as unknown as React.ChangeEvent);
    if (option) {
      onOptionChange?.(option);
    }
    onValueChange?.(nextValue);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setOpenState(nextOpen);
    if (!nextOpen) setQuery(null);
    onVisibleChange?.(nextOpen);
  };

  const sharedRootProps = {
    value: selectedOption,
    onValueChange: (
      option: O | null,
      details: { reason: string; cancel: () => void; allowPropagation: () => void },
    ) => {
      // Base UI's combobox clears the value on Escape and stops the key there. Keep the value unless
      // the select is clearable, and let Escape close an enclosing popover or dialog.
      if (details.reason === "escape-key") {
        details.allowPropagation();
        if (!clearable) {
          details.cancel();
          return;
        }
      }
      handleChange(option);
    },
    onOpenChange: handleOpenChange,
    open,
    isItemEqualToValue: (item: O, current: O) => item.value === current.value,
    itemToStringLabel: (item: O) => item.label,
    itemToStringValue: (item: O) => String(item.value),
    disabled,
    name,
    required,
    modal: false,
  };

  const rootProps = {
    ref: rootRef,
    className: clsx(styles.root, className),
    "data-size": size,
    "data-error": (ariaInvalid ?? Boolean(error)) || undefined,
  };

  const controlAria = {
    "aria-label": ariaLabel ?? (id || ariaLabelledBy ? undefined : resolvedPlaceholder),
    "aria-labelledby": ariaLabelledBy,
    "aria-describedby": ariaDescribedBy,
    "aria-invalid": ariaInvalid ?? (error ? true : undefined),
    "aria-required": required,
  };

  const positionerProps = getFloatingPositionerProps(
    { placement: "bottom-start", offset: 6 },
    { positionMethod: menuPosition ?? (container === rootRef.current ? "absolute" : "fixed") },
  );
  const portalProps = getFloatingPortalProps(rootRef.current, container);
  const menuClassName = clsx(FLOATING_POPUP_CLASS, FLOATING_PANEL_CLASS, styles.menu);
  const menuStyle = { "--calmlens-select-menu-min-width": menuMinWidth } as React.CSSProperties;

  // Each option's actions, rendered once per render; the keyboard shortcut only opens an option
  // that has some.
  const optionActions = new Map<string, React.ReactNode>();
  if (renderOptionActions) {
    for (const option of options) {
      const key = optionKey(option);
      const actions = renderOptionActions(option, {
        visible: actionsKey === key,
        onVisibleChange: (next) =>
          setActionsKey((current) => (next ? key : current === key ? null : current)),
        finalFocus: () => inputRef.current ?? triggerRef.current,
      });
      if (actions) optionActions.set(key, actions);
    }
  }
  /** Shift+F10 or the context menu key opens the highlighted option's actions. */
  const openHighlightedActions = (event: React.KeyboardEvent) => {
    if (!isActionsKey(event) || !open) return;
    const key = popupRef.current
      ?.querySelector<HTMLElement>("[data-highlighted][data-option-key]")
      ?.getAttribute("data-option-key");
    if (!key || !optionActions.has(key)) return;
    event.preventDefault();
    setActionsKey(key);
  };
  /** An option's row: the option, and its actions beside it so pressing them doesn't select it. */
  const renderRow = (option: O, item: React.ReactElement) => {
    const key = optionKey(option);
    const actions = optionActions.get(key);
    if (!actions) return item;
    return (
      <div key={key} role="none" className={styles.optionRow}>
        {item}
        {/* The actions' menu renders in a portal, but its events bubble through React to the list,
            which would select the highlighted option on Enter or move its highlight. */}
        {/* biome-ignore lint/a11y/noStaticElementInteractions: only stops events from the actions' own controls */}
        <span
          className={styles.optionActions}
          onKeyDown={stopPropagation}
          onClick={stopPropagation}
          onPointerDown={stopPropagation}
          onMouseDown={stopPropagation}
        >
          {actions}
        </span>
      </div>
    );
  };

  const renderOptionContent = (option: O) => (
    <>
      <span className={styles.optionContent}>
        <span className={styles.optionLabel}>{renderOption?.(option) ?? option.label}</span>
        {option.description ? (
          <span className={styles.optionDescription}>{option.description}</span>
        ) : null}
      </span>
      <IconCheck
        size={17}
        aria-hidden={true}
        className={styles.selectedIcon}
        data-visible={selectedOption?.value === option.value || undefined}
      />
    </>
  );

  const footer = children ? <div className={styles.menuFooter}>{children}</div> : null;
  const loadingNotice = loading ? <div className={styles.menuNotice}>{loadingText}</div> : null;
  const loadingIndicator = loading ? <span className={styles.loading} aria-hidden={true} /> : null;

  if (!searchable || disableSearch) {
    return (
      <BaseSelect.Root<O> {...sharedRootProps} id={inputId}>
        <div {...rootProps}>
          <BaseSelect.Trigger
            ref={composeRefs(ref as React.Ref<HTMLButtonElement>, triggerRef)}
            className={styles.control}
            data-invalid={controlAria["aria-invalid"] || undefined}
            onFocus={onFocus as React.FocusEventHandler<HTMLButtonElement>}
            onBlur={onBlur as React.FocusEventHandler<HTMLButtonElement>}
            {...controlAria}
          >
            {startAdornment ? <span className={styles.adornment}>{startAdornment}</span> : null}
            <BaseSelect.Value className={styles.value}>
              {(option: O | null) =>
                option ? (renderValue?.(option) ?? option.label) : resolvedPlaceholder
              }
            </BaseSelect.Value>
            {loadingIndicator}
            <BaseSelect.Icon className={styles.indicator}>
              <IconChevronDown size={18} aria-hidden={true} />
            </BaseSelect.Icon>
          </BaseSelect.Trigger>
        </div>
        <BaseSelect.Portal {...portalProps}>
          <BaseSelect.Positioner alignItemWithTrigger={false} {...positionerProps}>
            <BaseSelect.Popup
              ref={popupRef}
              className={menuClassName}
              style={menuStyle}
              onKeyDown={openHighlightedActions}
            >
              {loadingNotice}
              {!loading && options.length === 0 ? (
                <div className={styles.menuNotice}>{noOptionsText}</div>
              ) : null}
              <BaseSelect.List className={styles.menuList}>
                {options.map((option) =>
                  renderRow(
                    option,
                    <BaseSelect.Item
                      key={optionKey(option)}
                      value={option}
                      label={option.label}
                      disabled={Boolean(option.disabled)}
                      className={styles.option}
                      data-option-key={optionKey(option)}
                      aria-keyshortcuts={
                        optionActions.has(optionKey(option)) ? "Shift+F10" : undefined
                      }
                    >
                      {renderOptionContent(option)}
                    </BaseSelect.Item>,
                  ),
                )}
              </BaseSelect.List>
              {footer}
            </BaseSelect.Popup>
          </BaseSelect.Positioner>
        </BaseSelect.Portal>
      </BaseSelect.Root>
    );
  }

  return (
    <BaseCombobox.Root<O>
      {...sharedRootProps}
      items={options}
      // Every option shows until the user types; the selected label in the input isn't a search.
      filter={(option: O) => !filterOptions || query === null || matchesFilter(option, query)}
      inputValue={query ?? selectedOption?.label ?? ""}
      onInputValueChange={(inputValue, details) => {
        if (details.reason === "input-change") {
          setQuery(inputValue);
          onSearchChange?.(inputValue);
        } else {
          setQuery(null);
        }
      }}
      autoHighlight={true}
      openOnInputClick={true}
      open={open}
      onOpenChange={handleOpenChange}
    >
      <div {...rootProps}>
        {/* biome-ignore lint/a11y/noStaticElementInteractions: pointer-only shortcut to the input, which keyboard users reach directly */}
        <div
          ref={controlRef}
          className={styles.control}
          data-invalid={controlAria["aria-invalid"] || undefined}
          data-disabled={disabled || undefined}
          onMouseDown={(event) => {
            // The input is shorter than the control. A press on the rest of the box (its padding, the
            // gaps) acts like a press on the input instead of focusing nothing.
            const input = inputRef.current;
            if (disabled || !input || event.button !== 0) return;
            // Presses in the portalled list bubble here through React.
            if (!event.currentTarget.contains(event.target as Node)) return;
            if ((event.target as Element).closest("input, button")) return;
            event.preventDefault();
            input.focus();
            if (query === null) input.select();
            openCombobox();
          }}
        >
          {startAdornment ? <span className={styles.adornment}>{startAdornment}</span> : null}
          <BaseCombobox.Input
            ref={composeRefs(ref as React.Ref<HTMLInputElement>, inputRef)}
            id={inputId}
            className={styles.input}
            placeholder={resolvedPlaceholder}
            // Base UI opens on presses on the input itself. Clicking the field's <label> only sends
            // the input a click, so open from that too. Selecting the shown label lets typing replace
            // it with a search.
            onClick={(event) => {
              if (query === null) event.currentTarget.select();
              openCombobox();
            }}
            // Tabbing in also opens it. The Tab key goes down on the previous field and comes up here,
            // so focus moved by code (a dialog handing focus back) leaves the menu closed.
            onKeyUp={(event) => {
              if (event.key === "Tab") openCombobox();
            }}
            onKeyDown={openHighlightedActions}
            onFocus={onFocus as React.FocusEventHandler<HTMLInputElement>}
            onBlur={onBlur as React.FocusEventHandler<HTMLInputElement>}
            {...controlAria}
          />
          {loadingIndicator}
          {clearable && selectedOption ? (
            <BaseCombobox.Clear className={styles.indicator} aria-label={clearLabel}>
              <IconX size={16} aria-hidden={true} />
            </BaseCombobox.Clear>
          ) : null}
          <BaseCombobox.Trigger className={styles.indicator} tabIndex={-1} aria-hidden={true}>
            <IconChevronDown size={18} aria-hidden={true} />
          </BaseCombobox.Trigger>
        </div>
      </div>
      <BaseCombobox.Portal {...portalProps}>
        <BaseCombobox.Positioner anchor={controlRef} {...positionerProps}>
          <BaseCombobox.Popup ref={popupRef} className={menuClassName} style={menuStyle}>
            {loadingNotice}
            {loading ? null : (
              <BaseCombobox.Empty className={styles.menuNotice}>{noOptionsText}</BaseCombobox.Empty>
            )}
            <BaseCombobox.List className={styles.menuList}>
              {(option: O) =>
                renderRow(
                  option,
                  <BaseCombobox.Item
                    key={optionKey(option)}
                    value={option}
                    disabled={Boolean(option.disabled)}
                    className={styles.option}
                    data-option-key={optionKey(option)}
                    aria-keyshortcuts={
                      optionActions.has(optionKey(option)) ? "Shift+F10" : undefined
                    }
                  >
                    {renderOptionContent(option)}
                  </BaseCombobox.Item>,
                )
              }
            </BaseCombobox.List>
            {footer}
          </BaseCombobox.Popup>
        </BaseCombobox.Positioner>
      </BaseCombobox.Portal>
    </BaseCombobox.Root>
  );
};

export default Select;
